import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useReportControl } from '@/hooks/useReports'
import { FiltrosReporte, ControlResponse } from '@/services/reportService'
import ReportCard from './ReportCard'
import ControlArbolTab from './ControlArbolTab'
import ControlGrafoTab from './ControlGrafoTab'
import { formatCurrency } from '@/utils/currencyHelpers'

interface ControlReportProps {
  filtros: FiltrosReporte & { mostrar_solo_problemas?: boolean }
  onRefresh?: () => void
}

const ControlReport: React.FC<ControlReportProps> = ({ filtros, onRefresh }) => {
  const { colors } = useTheme()
  const { data: reportData, isLoading, error, refetch } = useReportControl(filtros)

  const [activeTab, setActiveTab] = React.useState<'arbol' | 'grafo'>('arbol')

  const handleRefresh = () => {
    refetch()
    onRefresh?.()
  }

  const getErrorMessage = () => {
    if (!error) return undefined
    const errorString = error instanceof Error ? error.message : String(error)
    return errorString
  }

  const handleExport = () => {
    if (!reportData?.tipos) return

    // Generar CSV con estructura jerárquica
    const csv: string[] = ['Tipo,Espacio,Reserva,Pago,Estado,Monto,Problema']

    for (const tipo of reportData.tipos) {
      for (const espacio of tipo.espacios) {
        for (const reserva of espacio.reservas) {
          for (const pago of reserva.pagos) {
            csv.push(
              [
                tipo.nombre,
                espacio.nombre,
                reserva.codigo,
                pago.id,
                pago.estado,
                pago.monto.toFixed(2),
                pago.validacion.mensaje
              ].join(',')
            )
          }
          // Si no hay pagos, mostrar solo la reserva
          if (reserva.pagos.length === 0) {
            csv.push(
              [tipo.nombre, espacio.nombre, reserva.codigo, '', '', '', reserva.validacion.mensaje].join(',')
            )
          }
        }
      }
    }

    const blob = new Blob([csv.join('\n')], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `control-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  return (
    <ReportCard
      title="Control - Jerarquía de Reservas, Pagos y DTEs"
      subtitle="Monitoreo con validaciones y drill-down por niveles"
      loading={isLoading}
      error={getErrorMessage()}
      onExport={handleExport}
      chartComponent={
        <div className="w-full">
          {/* Tabs - GRAFO INHABILITADO */}
          <div className="mb-4">
            <div className="flex gap-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('arbol')}
                className="px-6 py-3 font-semibold transition-all border-b-2"
                style={{
                  borderColor: activeTab === 'arbol' ? colors.primary : 'transparent',
                  color: activeTab === 'arbol' ? colors.primary : '#666'
                }}
              >
                🌳 Árbol Jerárquico
              </button>
              {/* Grafo tab inhabilitado temporalmente
              <button
                onClick={() => setActiveTab('grafo')}
                className="px-6 py-3 font-semibold transition-all border-b-2 opacity-50 cursor-not-allowed"
                disabled
                style={{
                  borderColor: 'transparent',
                  color: '#999'
                }}
                title="En desarrollo"
              >
                🕸️ Grafo Interactivo
              </button>
              */}
            </div>
          </div>

          {/* Contenido de Árbol */}
          {activeTab === 'arbol' && reportData && (
            <ControlArbolTab tipos={reportData.tipos} />
          )}

          {/* Contenido de Grafo - INHABILITADO */}
          {/* {activeTab === 'grafo' && reportData && (
            <ControlGrafoTab tipos={reportData.tipos} />
          )} */}
        </div>
      }
      tableComponent={<div />}
    >
      {/* Resumen KPIs */}
      {reportData?.resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-0.5">Total Reservas</p>
            <p className="text-xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.total_reservas}
            </p>
          </div>

          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-0.5">Total Pagos</p>
            <p className="text-xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.total_pagos}
            </p>
          </div>

          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-0.5">Total DTEs</p>
            <p className="text-xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.total_dtes}
            </p>
          </div>

          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: '#ef444415',
              borderLeft: '4px solid #ef4444'
            }}
          >
            <p className="text-xs text-gray-600 mb-0.5">⚠️ Problemas</p>
            <p className="text-xl font-bold" style={{ color: '#ef4444' }}>
              {reportData.resumen.problemas_detectados}
            </p>
          </div>
        </div>
      )}

      {/* Detalles de problemas */}
      {reportData?.resumen && reportData.resumen.problemas_detectados > 0 && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <h3 className="text-sm font-bold text-red-800 mb-2">Problemas Detectados</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {reportData.resumen.reservas_sin_pago > 0 && (
              <div className="bg-white p-3 rounded border-l-4 border-red-500">
                <p className="text-xs font-semibold text-gray-700">Reservas sin pago</p>
                <p className="text-xl font-bold text-red-600">{reportData.resumen.reservas_sin_pago}</p>
              </div>
            )}

            {reportData.resumen.pagos_estado_incorrecto > 0 && (
              <div className="bg-white p-3 rounded border-l-4 border-yellow-500">
                <p className="text-xs font-semibold text-gray-700">Pagos con estado incorrecto</p>
                <p className="text-xl font-bold text-yellow-600">{reportData.resumen.pagos_estado_incorrecto}</p>
              </div>
            )}

            {reportData.resumen.pagos_manuales_sin_adjunto > 0 && (
              <div className="bg-white p-3 rounded border-l-4 border-yellow-500">
                <p className="text-xs font-semibold text-gray-700">Pagos manuales sin comprobante</p>
                <p className="text-xl font-bold text-yellow-600">{reportData.resumen.pagos_manuales_sin_adjunto}</p>
              </div>
            )}

            {reportData.resumen.pagos_manuales_sin_referencia > 0 && (
              <div className="bg-white p-3 rounded border-l-4 border-yellow-500">
                <p className="text-xs font-semibold text-gray-700">Pagos manuales sin referencia</p>
                <p className="text-xl font-bold text-yellow-600">{reportData.resumen.pagos_manuales_sin_referencia}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </ReportCard>
  )
}

export default ControlReport
